import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import VerificationCodeInput from "../components/VerificationCodeInput";
import { useWhitelabel } from "../context/WhitelabelContext";
import { useAuth } from "../context/AuthContext";
import { getLogoMode, hostname } from "../utils";

interface MfaSetupProps {
  tempToken: string;
  email: string;
  uid: string;
  onComplete: (token: string) => void;
}

const MfaSetup: React.FC<MfaSetupProps> = ({
  tempToken,
  email,
  uid,
  onComplete,
}) => {
  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState<"loading" | "scan" | "verify">("loading");
  const { whitelabelLogos, whitelabelSettings } = useWhitelabel();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const mode = theme.palette.mode;

  const getLoginLogo = () => {
    if (mode === "dark" && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  useEffect(() => {
    setupMfa();
  }, []);

  const setupMfa = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${hostname}/auth/mfa/setup`,
        {},
        {
          headers: {
            Authorization: `Bearer ${tempToken}`,
          },
        }
      );

      if (response.data.success) {
        setQrCode(response.data.qrCode);
        setSecret(response.data.secret);
        setStep("scan");
      } else {
        throw new Error(response.data.error || "Failed to setup MFA");
      }
    } catch (err: any) {
      console.error("Error setting up MFA:", err);
      setError(
        err.response?.data?.error || "Failed to initialize MFA setup. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (code: string) => {
    setVerifying(true);
    setError("");

    try {
      const response = await axios.post(
        `${hostname}/auth/mfa/verify-setup`,
        { code },
        {
          headers: {
            Authorization: `Bearer ${tempToken}`,
          },
        }
      );

      if (response.data.success) {
        // MFA setup complete, now complete the login
        onComplete(tempToken);
      } else {
        throw new Error(response.data.error || "Invalid verification code");
      }
    } catch (err: any) {
      console.error("Error verifying MFA code:", err);
      setError(
        err.response?.data?.error || "Invalid verification code. Please try again."
      );
    } finally {
      setVerifying(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Setting up MFA...</Typography>
        </Box>
      );
    }

    return (
      <>
        <Box
          sx={{
            p: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={getLoginLogo()}
            alt="logo"
            style={{
              objectFit: "contain",
              maxWidth: 200,
              maxHeight: 60,
              verticalAlign: "middle",
            }}
          />
        </Box>

        <Box sx={{ textAlign: "center", px: 3, pb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Set Up Two-Factor Authentication
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Your organization requires MFA for enhanced security
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 3, mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {step === "scan" && (
          <>
            <Box sx={{ px: 3 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Step 1:</strong> Scan the QR code with your authenticator app
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use Google Authenticator, Authy, or any TOTP-compatible app
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                my: 2,
              }}
            >
              <Box
                component="img"
                src={qrCode}
                alt="MFA QR Code"
                sx={{
                  width: 200,
                  height: 200,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              />
            </Box>

            <Box sx={{ px: 3, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Can't scan? Enter this code manually:
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                  wordBreak: "break-all",
                  textAlign: "center",
                }}
              >
                {secret}
              </Box>
            </Box>

            <Box sx={{ px: 3, pb: 3 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setStep("verify")}
                sx={{ py: 1.5 }}
              >
                Continue to Verification
              </Button>
            </Box>
          </>
        )}

        {step === "verify" && (
          <>
            <Box sx={{ px: 3 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Step 2:</strong> Enter the 6-digit code from your app
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This confirms your authenticator is set up correctly
              </Typography>
            </Box>

            <Box sx={{ px: 3, pb: 2 }}>
              <VerificationCodeInput
                onComplete={handleVerifyCode}
                disabled={verifying}
                error={!!error}
                helperText={verifying ? "Verifying..." : ""}
              />
            </Box>

            <Box sx={{ px: 3, pb: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setStep("scan")}
                disabled={verifying}
              >
                Back to QR Code
              </Button>
            </Box>
          </>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: isMobile ? "flex-start" : "center",
        minHeight: "100vh",
        width: "100vw",
        p: isMobile ? 2 : 0,
        pt: isMobile ? 4 : 0,
      }}
    >
      {isMobile ? (
        <Box sx={{ width: "100%", maxWidth: 450 }}>{renderContent()}</Box>
      ) : (
        <Paper sx={{ width: 450 }}>{renderContent()}</Paper>
      )}
    </Box>
  );
};

export default MfaSetup;
